import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import api from '../services/api'

interface AgencyUser {
  id: string
  agencies: {
    id: string
    name: string
  }
  role: string
}

interface CompanyUserAccess {
  company_id: string
  role: string
  can_read: boolean | number
  can_write: boolean | number
}

interface User {
  id: string
  email: string
  name: string
  is_system_admin: boolean | number
  createdAt: string
  agency_users?: AgencyUser[]
  company_user_access?: CompanyUserAccess[]
}

interface Impersonation {
  active: boolean
  admin: { id: string; name: string } | null
}

interface AuthContextType {
  user: User | null
  loading: boolean
  impersonation: Impersonation | null
  login: (email: string, password: string) => Promise<void>
  signup: (firstName: string, lastName: string, email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  impersonateUser: (userId: string) => Promise<void>
  stopImpersonating: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [impersonation, setImpersonation] = useState<Impersonation | null>(null)

  const checkAuth = async () => {
    setLoading(true)
    try {
      const res = await api.get('/api/user')
      setUser(res.data.user)
      setImpersonation(res.data.impersonation ?? null)
    } catch {
      setUser(null)
      setImpersonation(null)
    } finally {
      setLoading(false)
    }
  }

  // System admin "log in as" another user, then reload the session as them.
  const impersonateUser = async (userId: string) => {
    await api.post(`/api/impersonate/${userId}`)
    await checkAuth()
  }

  // Return to the admin's own session.
  const stopImpersonating = async () => {
    await api.post('/api/impersonate/stop')
    await checkAuth()
  }

  useEffect(() => {
    checkAuth()
  }, [])

  const login = async (email: string, password: string) => {
    setLoading(true)
    try {
      await api.post('/api/auth/login', { email, password })
      await checkAuth()
    } finally {
      setLoading(false)
    }
  }

  const signup = async (firstName: string, lastName: string, email: string, password: string) => {
    setLoading(true)
    try {
      await api.post('/api/auth/signup', { firstName, lastName, email, password })
      await checkAuth()
    } finally {
      setLoading(false)
    }
  }

  const logout = async () => {
    setLoading(true)
    try {
      await api.post('/api/auth/logout', {})
      setUser(null)
      setImpersonation(null)
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        impersonation,
        login,
        signup,
        logout,
        checkAuth,
        impersonateUser,
        stopImpersonating,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
